import React, { useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Avatar,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OutboxIcon from "@mui/icons-material/Outbox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CInput2 from "../../../basecomp/components/CInput2";
import CButton from "../../../basecomp/components/CButton2";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import SignaturePad from "../../../basecomp/components/SignaturePad";
import useFullscreen, {
  isTouchDevice,
  isFullscreenSupported,
} from "../../../basecomp/base/useFullscreen";
import useKeyboardUnlock from "../../../basecomp/base/useKeyboardUnlock";
import IssueVm from "../viewmodel/IssueVm";

// Issue = green, per the app-wide color coding (Receive = blue).
const ACCENT = "#3CB043";

// Order the scanner/keyboard "Enter" jumps through, one row at a time.
const FIELD_ORDER = [
  "material_no",
  "material_desc",
  "qty",
  "uom",
  "storage_loc",
  "storage_bin",
  "remarks",
];

const focusField = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.focus();
  }
};

// Restyles every CInput2 / CButton rendered underneath (both are shared,
// unstyled components) into a rounded, "glowing focus" modern skin —
// scoped to this page only via descendant selectors, no shared file touched.
const modernSkinSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    transition: "box-shadow .18s ease, background-color .18s ease",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: "#ffffff",
    boxShadow: `0 0 0 4px ${ACCENT}1f`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
    borderWidth: "1.5px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: ACCENT,
  },
  "& .MuiButton-root": {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    paddingInline: "1.5em",
    paddingBlock: "0.6em",
    lineHeight: 1.4,
  },
  "& .MuiButton-containedPrimary:hover": {
    boxShadow: `0 6px 16px ${ACCENT}33`,
  },
  "& .MuiIconButton-root": {
    borderRadius: "10px",
  },
};

// Scoped override — makes just the Cancel button read as a secondary/outlined
// action instead of the same solid green as Save, without touching CButton2.
const secondaryButtonSx = {
  "& .MuiButton-root": {
    backgroundColor: "#fff",
    color: ACCENT,
    border: `1.5px solid ${ACCENT}33`,
  },
  "& .MuiButton-root:hover": {
    backgroundColor: `${ACCENT}0a`,
  },
};

const PageBg = styled(Box)({
  minHeight: "100%",
  background: "linear-gradient(180deg, #f5f7fb 0%, #eef1f6 100%)",
  padding: "5em 1.5em 3em 1.5em",
});

const PageContent = styled(Box)({
  maxWidth: "1400px",
  margin: "0 auto",
});

const Card = styled(Paper)({
  borderRadius: "1.25em",
  padding: "2em",
  boxShadow: "0 8px 28px rgba(15, 23, 42, 0.07)",
  border: "1px solid rgba(15, 23, 42, 0.05)",
});

const SectionBanner = styled("div")({
  textAlign: "center",
  backgroundColor: ACCENT,
  color: "white",
  padding: ".6em",
  borderRadius: "0.9em 0.9em 0 0",
  fontSize: "1em",
  fontWeight: 600,
  letterSpacing: ".02em",
});

const RowBadge = styled(Avatar)({
  width: 26,
  height: 26,
  fontSize: "0.75em",
  fontWeight: 700,
  backgroundColor: "#eef1f6",
  color: ACCENT,
});

const IssueView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    work_order_no,
    setWorkOrderNo,
    issued_to,
    setIssuedTo,
    collected_by,
    setCollectedBy,
    remarks,
    setRemarks,
    signature,
    setSignature,
    rows,
    updateRowField,
    lookupMaterial,
    selectLocation,
    notFoundMaterialNo,
    dismissNotFound,
    addMoreRows,
    removeRow,
    handleSave,
    loading,
    editingId,
    resetForm,
    formReady,
  } = IssueVm();

  const { isFullscreen, toggleFullscreen, droppedUnexpectedly, resumeFullscreen } = useFullscreen();
  const touchDevice = isTouchDevice();
  const { getFieldProps, getResetKey, unlockField } = useKeyboardUnlock(touchDevice);

  useEffect(() => {
    if (formReady) {
      focusField("collected_by_input");
    }
  }, [formReady]);

  const focusNextField = (rowIndex, currentKey) => {
    const idx = FIELD_ORDER.indexOf(currentKey);
    if (idx >= 0 && idx < FIELD_ORDER.length - 1) {
      focusField(`${FIELD_ORDER[idx + 1]}_input_${rowIndex}`);
    } else {
      focusField(`material_no_input_${rowIndex + 1}`);
    }
  };

  // Waits for the sq01 lookup to resolve before moving focus, since a locked
  // (disabled) field can never receive focus() — jump straight to the next
  // row's Material Number when Description/UOM (and the default Qty=1) got
  // auto-filled, ready for the next scan; Qty is left for the user to click
  // into manually if they need to change it from the default. Stay on
  // Material Number (now cleared, ready for the next scan) if it was a
  // re-scan of an existing line or nothing was found at all, otherwise fall
  // through to Description as usual so the user can type it manually.
  // The other "user is actually done" signal, alongside Enter below: leaving
  // the field. Deliberately does NOT chase focus anywhere afterward (unlike
  // handleMaterialNoSubmit) — the user already chose where to go next by
  // clicking/tabbing there, and yanking focus away again would fight that.
  // Guarded so it can't double-fire right after Enter's own lookup already
  // locked the row (a focusField() call moving focus elsewhere triggers a
  // blur too), and does nothing on an empty field.
  const handleMaterialNoBlur = (rowIndex) => {
    const row = rows[rowIndex];
    if (row?.material_no && !row.material_locked) {
      lookupMaterial(rowIndex);
    }
  };

  const handleMaterialNoSubmit = async (rowIndex) => {
    const result = await lookupMaterial(rowIndex);
    // Deferred: a newly auto-grown row is pushed inside lookupMaterial's own
    // setRows call, so it isn't in the DOM yet when this resolves — focusing
    // now would silently no-op (see goToNextRow, which needs the same fix).
    if (result === "not_found" || result === "duplicate") {
      setTimeout(() => focusField(`material_no_input_${rowIndex}`), 0);
    } else if (result === true) {
      setTimeout(() => focusField(`material_no_input_${rowIndex + 1}`), 0);
    } else {
      setTimeout(() => focusField(`material_desc_input_${rowIndex}`), 0);
    }
  };

  // Manual escape hatch to the next row's Material Number — in addition to
  // Enter-on-Remarks already doing this automatically. Ensures a next row
  // exists (auto-grow may not have kicked in yet) before moving focus.
  const goToNextRow = (rowIndex) => {
    if (rowIndex === rows.length - 1) {
      addMoreRows();
    }
    setTimeout(() => focusField(`material_no_input_${rowIndex + 1}`), 0);
  };

  const filledCount = rows.filter((row) => row.material_no && row.qty).length;

  // No Work Order number resolved yet (still checking the URL / loading an
  // existing record, or about to redirect back) — don't render the form.
  if (!formReady) {
    return <CBackDrop cOpen />;
  }

  return (
    <PageBg>
      <PageContent sx={modernSkinSx}>
        <CBackDrop cOpen={loading} />

        <Dialog open={!!notFoundMaterialNo} onClose={dismissNotFound}>
          <DialogTitle>{t("material_not_found_title")}</DialogTitle>
          <DialogContent>
            <Typography>
              {t("material_not_found_desc")} "{notFoundMaterialNo}"
            </Typography>
          </DialogContent>
          <DialogActions>
            <CButton cText={t("ok")} cTooltipText="ok" cOnClick={dismissNotFound} />
          </DialogActions>
        </Dialog>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginBottom: "1.5em" }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={t("back")}>
              <IconButton onClick={() => navigate("/warehouse/issue-receive")}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Avatar sx={{ bgcolor: `${ACCENT}1f`, color: ACCENT, width: 48, height: 48 }}>
              <OutboxIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                {t("issue_material")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("issue_material_desc")}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Card>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: "0 0 22%" }}>
              <CInput2
                cId="work_order_no_input"
                cHintText={t("work_order_number")}
                cValue={work_order_no}
                cOnChange={setWorkOrderNo}
                cIsRequired
                cFullWidth
                cDisabled
                cHandleSubmit={() => focusField("collected_by_input")}
                cHtmlProps={getFieldProps("work_order_no_input")}
                cKeyReset={getResetKey("work_order_no_input")}
                cOnUnlock={() => unlockField("work_order_no_input")}
              />
            </Box>
            <Box sx={{ flex: "0 0 22%" }}>
              <CInput2
                cId="issued_to_input"
                cHintText={t("issued_to")}
                cValue={issued_to}
                cOnChange={setIssuedTo}
                cIsRequired
                cFullWidth
                cDisabled
                cHandleSubmit={() => focusField("collected_by_input")}
                cHtmlProps={getFieldProps("issued_to_input")}
                cKeyReset={getResetKey("issued_to_input")}
                cOnUnlock={() => unlockField("issued_to_input")}
              />
            </Box>
            <Box sx={{ flex: "0 0 22%" }}>
              <CInput2
                cId="collected_by_input"
                cHintText={t("collected_by")}
                cValue={collected_by}
                cOnChange={setCollectedBy}
                cIsRequired
                cFullWidth
                cHandleSubmit={() => focusField("header_remarks_input")}
                cHtmlProps={getFieldProps("collected_by_input")}
                cKeyReset={getResetKey("collected_by_input")}
                cOnUnlock={() => unlockField("collected_by_input")}
              />
            </Box>
            <Box sx={{ flex: "0 0 auto" }}>
              <SignaturePad value={signature} onChange={setSignature} accent={ACCENT} />
            </Box>
            <Box sx={{ flex: "1 1 auto" }}>
              <CInput2
                cId="header_remarks_input"
                cHintText={t("remarks")}
                cValue={remarks}
                cOnChange={setRemarks}
                cFullWidth
                cHandleSubmit={() => focusField("material_no_input_0")}
                cHtmlProps={getFieldProps("header_remarks_input")}
                cKeyReset={getResetKey("header_remarks_input")}
                cOnUnlock={() => unlockField("header_remarks_input")}
              />
            </Box>
          </Stack>

          <Box sx={{ marginTop: "2em" }}>
            <SectionBanner>{t("material_number")}</SectionBanner>
            <Box
              sx={{
                border: `1px solid ${ACCENT}`,
                borderTop: "none",
                borderRadius: "0 0 1em 1em",
                overflowX: "auto",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { backgroundColor: "#f1f4f9", fontWeight: 700, fontSize: ".8em", whiteSpace: "nowrap" } }}>
                    <TableCell sx={{ width: "2.5em" }} />
                    <TableCell>{t("material_number")}</TableCell>
                    <TableCell>{t("material_description")}</TableCell>
                    <TableCell>{t("quantity")}</TableCell>
                    <TableCell>{t("uom")}</TableCell>
                    <TableCell>{t("storage_location")}</TableCell>
                    <TableCell>{t("storage_bin")}</TableCell>
                    <TableCell>{t("remarks")}</TableCell>
                    <TableCell align="center">{t("Delete")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          goToNextRow(idx);
                        }
                      }}
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "#fafbfd" },
                        "&:hover": { backgroundColor: "#eef8ef" },
                        "&:focus-within": { backgroundColor: `${ACCENT}0d` },
                        transition: "background-color .15s ease",
                      }}
                    >
                      <TableCell>
                        <RowBadge>{idx + 1}</RowBadge>
                      </TableCell>
                      <TableCell sx={{ minWidth: "9em" }}>
                        <CInput2
                          cId={`material_no_input_${idx}`}
                          cValue={row.material_no}
                          cOnChange={updateRowField(idx, "material_no")}
                          cFullWidth
                          cIsSmall
                          cHandleSubmit={() => handleMaterialNoSubmit(idx)}
                          cOnBlur={() => handleMaterialNoBlur(idx)}
                          cHtmlProps={getFieldProps(`material_no_input_${idx}`)}
                          cKeyReset={getResetKey(`material_no_input_${idx}`)}
                          cOnUnlock={() => unlockField(`material_no_input_${idx}`)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: "10em" }}>
                        <CInput2
                          cId={`material_desc_input_${idx}`}
                          cValue={row.material_desc}
                          cOnChange={updateRowField(idx, "material_desc")}
                          cFullWidth
                          cIsSmall
                          cDisabled={row.material_locked}
                          cHandleSubmit={() => focusNextField(idx, "material_desc")}
                          cHtmlProps={getFieldProps(`material_desc_input_${idx}`)}
                          cKeyReset={getResetKey(`material_desc_input_${idx}`)}
                          cOnUnlock={() => unlockField(`material_desc_input_${idx}`)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: "6em" }}>
                        <CInput2
                          cId={`qty_input_${idx}`}
                          cType="quantity"
                          cValue={row.qty}
                          cOnChange={updateRowField(idx, "qty")}
                          cFullWidth
                          cIsSmall
                          cHandleSubmit={() => focusNextField(idx, "qty")}
                          cHtmlProps={getFieldProps(`qty_input_${idx}`)}
                          cKeyReset={getResetKey(`qty_input_${idx}`)}
                          cOnUnlock={() => unlockField(`qty_input_${idx}`)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: "6em" }}>
                        <CInput2
                          cId={`uom_input_${idx}`}
                          cValue={row.uom}
                          cOnChange={updateRowField(idx, "uom")}
                          cFullWidth
                          cIsSmall
                          cDisabled={row.material_locked}
                          cHandleSubmit={() => focusNextField(idx, "uom")}
                          cHtmlProps={getFieldProps(`uom_input_${idx}`)}
                          cKeyReset={getResetKey(`uom_input_${idx}`)}
                          cOnUnlock={() => unlockField(`uom_input_${idx}`)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: "10em" }}>
                        {row.location_options.length > 1 ? (
                          <Select
                            id={`storage_loc_input_${idx}`}
                            size="small"
                            fullWidth
                            displayEmpty
                            value={row.storage_loc || ""}
                            onChange={(e) => {
                              const picked = row.location_options.find(
                                (loc) => loc.storage_loc === e.target.value
                              );
                              if (picked) {
                                selectLocation(idx, picked);
                              }
                            }}
                          >
                            <MenuItem value="" disabled>
                              {t("select_storage_location")}
                            </MenuItem>
                            {row.location_options.map((loc) => (
                              <MenuItem
                                key={`${loc.storage_loc}-${loc.bin_location}`}
                                value={loc.storage_loc}
                              >
                                {loc.storage_loc} — {loc.bin_location}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <CInput2
                            cId={`storage_loc_input_${idx}`}
                            cValue={row.storage_loc}
                            cOnChange={updateRowField(idx, "storage_loc")}
                            cFullWidth
                            cIsSmall
                            cDisabled={row.location_locked}
                            cHandleSubmit={() => focusNextField(idx, "storage_loc")}
                            cHtmlProps={getFieldProps(`storage_loc_input_${idx}`)}
                            cKeyReset={getResetKey(`storage_loc_input_${idx}`)}
                            cOnUnlock={() => unlockField(`storage_loc_input_${idx}`)}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: "8em" }}>
                        <CInput2
                          cId={`storage_bin_input_${idx}`}
                          cValue={row.storage_bin}
                          cOnChange={updateRowField(idx, "storage_bin")}
                          cFullWidth
                          cIsSmall
                          cDisabled={row.location_locked || row.location_options.length > 1}
                          cHandleSubmit={() => focusNextField(idx, "storage_bin")}
                          cHtmlProps={getFieldProps(`storage_bin_input_${idx}`)}
                          cKeyReset={getResetKey(`storage_bin_input_${idx}`)}
                          cOnUnlock={() => unlockField(`storage_bin_input_${idx}`)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: "8em" }}>
                        <CInput2
                          cId={`remarks_input_${idx}`}
                          cValue={row.remarks}
                          cOnChange={updateRowField(idx, "remarks")}
                          cFullWidth
                          cIsSmall
                          cHandleSubmit={() => focusNextField(idx, "remarks")}
                          cHtmlProps={getFieldProps(`remarks_input_${idx}`)}
                          cKeyReset={getResetKey(`remarks_input_${idx}`)}
                          cOnUnlock={() => unlockField(`remarks_input_${idx}`)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <CButton
                          cSrc={<DeleteIcon fontSize="small" sx={{ color: "#e53935" }} />}
                          cTooltipText="remove"
                          cOnClick={() => removeRow(idx)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ marginTop: "1.5em" }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CButton
                cText={t("add_row")}
                cSrc={<AddIcon />}
                cTooltipText="add_row"
                cOnClick={addMoreRows}
              />
              {filledCount > 0 && (
                <Chip
                  icon={<CheckCircleIcon sx={{ color: `${ACCENT} !important` }} />}
                  label={`${filledCount} material${filledCount > 1 ? "s" : ""} ready`}
                  sx={{ backgroundColor: `${ACCENT}1a`, color: ACCENT, fontWeight: 600 }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              {!editingId && (
                <Box sx={secondaryButtonSx}>
                  <CButton cText={t("cancel")} cTooltipText="cancel" cOnClick={resetForm} />
                </Box>
              )}
              <CButton cText={t("save")} cTooltipText="save" cOnClick={handleSave} />
            </Stack>
          </Stack>
        </Card>
      </PageContent>

      {isFullscreenSupported() && droppedUnexpectedly && (
        <Chip
          label={t("tap_to_resume_fullscreen")}
          onClick={resumeFullscreen}
          sx={{
            position: "fixed",
            bottom: "5.5em",
            right: "1.5em",
            zIndex: 1400,
            backgroundColor: ACCENT,
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.3)",
          }}
        />
      )}

      {isFullscreenSupported() && (
        <Tooltip title={t(isFullscreen ? "exit_full_screen" : "full_screen")}>
          <IconButton
            onClick={toggleFullscreen}
            sx={{
              position: "fixed",
              bottom: "1.5em",
              right: "1.5em",
              zIndex: 1400,
              width: 52,
              height: 52,
              borderRadius: "14px",
              backgroundColor: "#0f172a",
              color: "#22c55e",
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.35)",
              "&:hover": { backgroundColor: "#1e293b" },
            }}
          >
            {isFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
        </Tooltip>
      )}
    </PageBg>
  );
};

export default IssueView;
